import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import {
    faDogLeashed,
    faHotel,
    faScissors,
    faTennisBall,
    faVanShuttle,
    faWhistle
} from '@fortawesome/pro-regular-svg-icons';
import type { ListType } from 'components/Bookings2/List/AppointmentsList';
import {
    Waitlist,
    WaitlistKey,
    WaitlistPet,
    WaitlistService,
    WaitlistServiceKey,
    WaitlistTabCounts,
    WaitlistView
} from 'model/Waitlist';
import moment from 'moment-timezone';

// Day first, as in the design: "Thu, 8 Oct 2026".
const DATE_FORMAT = 'ddd, D MMM YYYY';

export const WAITLIST_VIEW_LABELS: Record<WaitlistView, string> = {
    latest: 'Latest',
    oldest: 'Oldest',
    expired: 'Expired'
};

export const WAITLIST_SERVICE_KEY_ICONS: Record<WaitlistServiceKey, IconDefinition> = {
    grooming: faScissors,
    daycare: faTennisBall,
    boarding: faHotel,
    transportation: faVanShuttle,
    walking: faDogLeashed,
    training: faWhistle
};

export const WAITLIST_SERVICE_KEY_LABELS: Record<WaitlistServiceKey, string> = {
    grooming: 'Grooming',
    daycare: 'Daycare',
    boarding: 'Boarding',
    transportation: 'Transportation',
    walking: 'Walking',
    training: 'Training'
};

// Bookings tabs where the Waitlist button is available, mapped to the API `key`.
const LIST_TYPE_KEYS: Partial<Record<ListType, WaitlistKey>> = {
    grooming: 'grooming',
    daycare: 'daycare',
    boarding: 'boarding',
    transportation: 'transportation',
    walking: 'walking',
    training: 'training',
    requested: 'all',
    all: 'all'
};

export const getWaitlistKeyForListType = (listType: ListType): WaitlistKey | undefined => LIST_TYPE_KEYS[listType];

// Dates come as plain calendar days ("YYYY-MM-DD"): parse them without timezone conversion.
export const formatWaitlistDate = (date: string, format = DATE_FORMAT): string =>
    moment(date, 'YYYY-MM-DD').format(format);

const plural = (count: number, word: string) => `${count} ${word}${count === 1 ? '' : 's'}`;

const toDay = (date: string) => moment(date, 'YYYY-MM-DD');

/**
 * Plain calendar days, each with its weekday: one day reads "Thu, 8 Oct 2026"; consecutive days read as a span
 * and loose days as a list, each month and year written once: "Wed 2 to Wed 9 Sep 2026",
 * "Sun 30 Aug to Sat 5 Sep 2026", "Wed 2, Fri 4, Sun 6 Sep 2026", "Wed 2 to Fri 4, Wed 9 Sep 2026",
 * "Wed 30 Dec 2026, Sat 2 Jan 2027".
 */
const formatDays = (dates: Array<string>): string => {
    const sorted = Array.from(new Set(dates)).sort();

    if (sorted.length === 0) {
        return 'ASAP';
    }

    if (sorted.length === 1) {
        return formatWaitlistDate(sorted[0]);
    }

    // Runs of consecutive days.
    const runs: Array<Array<string>> = [];
    sorted.forEach(date => {
        const run = runs[runs.length - 1];

        if (
            run &&
            toDay(run[run.length - 1])
                .add(1, 'day')
                .isSame(toDay(date), 'day')
        ) {
            run.push(date);
        } else {
            runs.push([date]);
        }
    });

    // Consecutive days collapse to "first to last"; loose days are listed.
    const items = runs.flatMap(run =>
        run.length > 1
            ? [
                  { date: run[0], joiner: ' to ' },
                  { date: run[run.length - 1], joiner: ', ' }
              ]
            : run.map(date => ({ date, joiner: ', ' }))
    );

    return items
        .map(({ date, joiner }, index) => {
            const day = toDay(date);
            const next = items[index + 1] && toDay(items[index + 1].date);
            const format =
                !next || !day.isSame(next, 'year')
                    ? 'ddd D MMM YYYY'
                    : day.isSame(next, 'month')
                    ? 'ddd D'
                    : 'ddd D MMM';

            return `${day.format(format)}${next ? joiner : ''}`;
        })
        .join('');
};

// Days a service covers: boarding dates are nights, so the stay also includes the check-out day.
const getServiceDays = (dates: Array<string>, businessArea: string): Array<string> => {
    if (businessArea !== 'boarding' || dates.length === 0) {
        return dates;
    }

    const lastNight = [...dates].sort()[dates.length - 1];

    return [...dates, toDay(lastNight).add(1, 'day').format('YYYY-MM-DD')];
};

/**
 * Dates of one service: "ASAP", "Thu, 8 Oct 2026", "Wed 2 to Wed 9 Sep 2026", "Wed 2, Fri 4, Sun 6 Sep 2026" or,
 * for boarding, the stay from check-in to check-out "Thu 8 to Sun 11 Oct 2026 · 3 nights".
 */
export const formatWaitlistServiceDates = (dates: Array<string>, businessArea: string): string => {
    const days = formatDays(getServiceDays(dates, businessArea));

    return businessArea === 'boarding' && dates.length > 0 ? `${days} · ${plural(dates.length, 'night')}` : days;
};

/** Every date asked for in the waitlist, in the same format as each service. */
export const formatWaitlistDates = (waitlist: Waitlist): string =>
    formatDays(waitlist.services.flatMap(service => getServiceDays(service.dates, service.businessArea)));

export const formatPreferredTime = (preferredTime: string | null): string =>
    preferredTime === null ? 'Anytime' : moment(preferredTime, 'HH:mm').format('h:mma');

/** Card header: "ASAP · Anytime · Hanne" (staff only when there is a preference). */
export const formatWaitlistPreferences = (waitlist: Waitlist): string => {
    const staff = Array.from(
        new Set(waitlist.services.map(service => service.preferredStaffName).filter(Boolean))
    ).join(', ');

    return [formatWaitlistDates(waitlist), formatPreferredTime(waitlist.preferredTime), staff]
        .filter(Boolean)
        .join(' · ');
};

const formatValidUntil = (waitlist: Waitlist): string => {
    if (waitlist.validUntil === null) {
        return 'Ongoing';
    }

    const date = formatWaitlistDate(waitlist.validUntil);

    return waitlist.state === 'expired' ? `expired on ${date}` : `valid until ${date}`;
};

/** Card footer: "Added on Wed, 28 Aug 2026 · 10:33am valid until Thu, 8 Oct 2026" (or "· Ongoing"). */
export const formatWaitlistCreatedAt = (waitlist: Waitlist): string => {
    const createdAt = `Added on ${waitlist.createdAt.format(DATE_FORMAT)} · ${waitlist.createdAt.format('h:mma')}`;

    return `${createdAt}${waitlist.validUntil === null ? ' · ' : ' '}${formatValidUntil(waitlist)}`;
};

export const getWaitlistCustomerName = (waitlist: Waitlist): string =>
    [waitlist.customerFirstName, waitlist.customerLastName].filter(Boolean).join(' ');

export const groupWaitlistServicesByPet = (services: Array<WaitlistService>): Array<WaitlistPet> =>
    services.reduce<Array<WaitlistPet>>((pets, service) => {
        const pet = pets.find(current => current.id === service.petId);

        if (pet) {
            pet.services.push(service);
            return pets;
        }

        return [
            ...pets,
            {
                id: service.petId,
                name: service.petName,
                image: service.petImage,
                type: service.petType,
                breedName: service.petBreedName,
                size: service.petSize,
                weight: service.petWeight,
                services: [service]
            }
        ];
    }, []);

export const getWaitlistCount = (counts: WaitlistTabCounts | undefined, key: WaitlistKey): number | undefined => {
    if (counts === undefined) {
        return undefined;
    }

    return key === 'all' ? counts.all : counts.counts[key] ?? 0;
};
